import { useState, useEffect, useCallback, useRef } from "react";

const usePaginate = ({
  apiMethod,
  data = {},
  defaultLoading = true,
  dataKey = "admins",
  filters = {},
  initialLimit = 10,
}) => {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(defaultLoading);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);

  const [sort, setSort] = useState({
    sortField: data.sortField || "createdAt",
    sortOrder: data.sortOrder || "desc",
  });

  const [params, setParams] = useState({
    ...data,
    page: 1,
    limit: data?.limit ?? initialLimit,
    filters,
    sortField: data.sortField || "createdAt",
    sortOrder: data.sortOrder || "desc",
  });

  const latestParams = useRef(params);
  const isInitialRender = useRef(true);
  const isManualFetch = useRef(false);

  const hasFiltersOrSortingChanged = (prevParams, newParams) => {
    const prevFilters = prevParams.filters || {};
    const newFilters = newParams.filters || {};

    const isAllFiltersBlank = (f) =>
      Object.values(f).every(
        (v) => v === "" || v === null || v === undefined
      );

    const hasFiltersChanged =
      JSON.stringify(prevFilters) !== JSON.stringify(newFilters) &&
      !(isAllFiltersBlank(prevFilters) && isAllFiltersBlank(newFilters));

    return (
      hasFiltersChanged ||
      prevParams.sortField !== newParams.sortField ||
      prevParams.sortOrder !== newParams.sortOrder ||
      prevParams.page !== newParams.page ||
      prevParams.limit !== newParams.limit
    );
  };

  const makeApiCall = useCallback(async () => {
    try {
      setLoading(true);

      if (typeof apiMethod !== "function") {
        throw new Error("apiMethod is not a valid function");
      }

      const { id, ...rest } = latestParams.current || {};

      const response = await apiMethod(
        id !== undefined && id !== null
          ? { id, body: rest }
          : { body: rest }
      );

      const decryptedData = response?.data?.data;

      if (decryptedData) {
        setResponseData(decryptedData[dataKey] || []);
        setTotalPages(decryptedData.pagination?.totalPages || 1);
        setTotalItems(decryptedData.pagination?.totalItems || 0);
        setLimit(decryptedData.pagination?.perPage || initialLimit);
        setError(null);
      } else {
        setError("Invalid data structure received.");
      }
    } catch (err) {
      setError(String(err?.response?.data?.message || err));
    } finally {
      setLoading(false);
    }
  }, [apiMethod, dataKey, initialLimit]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      latestParams.current = params;
      makeApiCall();
      return;
    }

    if (isManualFetch.current) {
      isManualFetch.current = false;
      return;
    }

    if (hasFiltersOrSortingChanged(latestParams.current, params)) {
      latestParams.current = params;
      makeApiCall();
    }
  }, [params, makeApiCall]);

  const fetchData = useCallback(() => {
    isManualFetch.current = true;
    makeApiCall();
  }, [makeApiCall]);

  const resetFilters = useCallback(() => {
    setParams((prev) => ({
      ...prev,
      filters: {},
      page: 1,
    }));
  }, []);

  const setPage = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    setParams((prev) => ({ ...prev, page: pageNumber }));
  }, []);

  const updateLimit = useCallback((newLimit) => {
    setLimit(newLimit);
    setParams((prev) => ({ ...prev, limit: newLimit }));
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setParams((prev) => ({
      ...prev,
      filters: Object.keys(newFilters).length ? newFilters : {},
      page: 1,
    }));
  }, []);

  const setSorting = useCallback((newSorting) => {
    setSort(newSorting);
    setParams((prev) => ({
      ...prev,
      sortField: newSorting.sortField,
      sortOrder: newSorting.sortOrder,
      page: 1,
    }));
  }, []);

  return {
    data: responseData,
    loading,
    error,
    fetchData,
    totalPages,
    totalItems,
    currentPage,
    setSorting,
    sort,
    setParams,
    limit,
    setLimit: updateLimit,
    setFilters: updateFilters,
    resetFilters,
    setPage,
  };
};

export default usePaginate;
